// src/data/reviewRecord.js
//
// THE AUDITED REVIEW RECORD - ruling 69, which closes the ISS-17 family.
//
// Bob, 2026-08-29: "Inside the General Committee, yes, up to a point - and it should be transparent,
// recorded, reasons why its being reviewed and if there are any concerns, questions, or conflicts,
// legal remedies and recourse are available and defined in the SMART Constitution..."
//
// ---------------------------------------------------------------------------------------------
// "A REVIEW IS NOT A READ. IT IS AN ACT, AND IT LEAVES A RECORD." - ruling 69.2
//
// That sentence is the whole module. Three conditions, and 69.2 lists them as three because Bob
// named them as three:
//
//   1. TRANSPARENT   the subordinate office KNOWS the review happened. "A silent review of an
//                    officer's work by his superior is the thing this ruling forbids by naming its
//                    opposite."
//   2. RECORDED      who reviewed, what, and when.
//   3. THE REASON    "Bob names this separately from 'recorded', so it is a SEPARATE requirement:
//                    not merely that a review occurred but WHY it was being conducted."
//
// SO THE REASON IS REQUIRED AND CANNOT BE SATISFIED BY A DEFAULT. A picklist of five reasons would
// technically record one; it would also mean nobody ever writes a reason again, and the requirement
// Bob stated separately would quietly collapse back into the one he stated before it.
//
// ---------------------------------------------------------------------------------------------
// WHAT THIS MODULE CANNOT DO, STATED PLAINLY BECAUSE IT MATTERS MORE THAN WHAT IT CAN.
//
// 69.2: "A security rule cannot compel a side effect (66.3), so the same limitation applies and the
// same answer: the read and the audit write live in one function, and A VERIFIER CHECK IS WHAT
// MAKES IT REAL."
//
// `firestore.rules` CANNOT force a reader to write an audit row. Nothing in Firestore can. So this
// module produces the record and the caller writes it, and the guarantee that it always happens is
// NOT in this file - it is in the check that reads production and reports reviews without records.
// Anyone reading this module should know that its enforcement lives somewhere else, because a
// reader who assumes the rules layer is holding this line will be wrong.
//
// ---------------------------------------------------------------------------------------------
// WHERE THE LINE IS NOT DRAWN, AND WHY THIS FILE DOES NOT DRAW IT.
//
// 69.1: "'Up to a point.' Bob did not draw the line and the assistant will not draw it for him."
//
// So mayReview() below tests the two things the ruling DID settle - inside the committee, and not a
// draft - and nothing else. There is no depth limit, no field-level redaction and no time window,
// because inventing one would be answering the question Bob left open.
// ---------------------------------------------------------------------------------------------

export const REASON_MIN = 8;
export const REASON_MAX = 400;

// What a review can be OF. Kept small and additive: a vocabulary invented ahead of the need is a
// vocabulary that misdescribes the first real case.
export const SUBJECTS = {
  CLAIM: 'claim',
  DISCIPLINE_CASE: 'discipline_case',
};

// WHO MAY REVIEW WHOM. Exactly what 69.1 settles and nothing further.
//
// The authorities are Article 21A SS5 (General Chairperson) and SS12 (Local Chairperson) - and the
// correction recorded at 69.3 is worth carrying here, because the wrong citation on a page a member
// reads is the kind of error that survives for years: 21A is the Railroad, Mechanical and
// Engineering Department. THE TRANSPORTATION DIVISION IS 21B, and 21B SS27 and SS28 are where the
// remedies live - Board of Appeals, and no resort to civil courts until appeals are exhausted.
export function mayReview(subject, reviewer) {
  if (!subject || !reviewer) return false;

  // NOT A DRAFT, EVER. Ruling 56 is undisturbed by 69 and 69.1 says so in terms.
  if (String(subject.status || '') === 'draft') return false;

  if (reviewer.isAdmin) return true;

  // "Inside the General Committee" - and not across committees.
  if (reviewer.role === 'general_chair') {
    return !!reviewer.committee && reviewer.committee === subject.committee;
  }

  // A Local Chairperson is not a superior office. He reads his own scope through the ordinary
  // claim rules; that is not a review and does not produce a record.
  return false;
}

// Why a review is refused, in the reviewer's own terms rather than as a denial.
export function reviewBlockedReason(subject, reviewer) {
  if (!subject || !reviewer) return '';
  if (String(subject.status || '') === 'draft') {
    return 'This is a draft. A draft belongs to its author alone and no office reviews '
         + 'it - not because of who you are, but because it has not been submitted to anyone yet. [R-56]';
  }
  if (reviewer.role === 'general_chair' && reviewer.committee !== subject.committee) {
    return 'Review runs inside a General Committee, not across them. This matter '
         + 'belongs to ' + (subject.committee || 'another committee') + '. [R-69.1]';
  }
  if (reviewer.role === 'local_chair') {
    return 'Review is a superior office looking at a subordinate\'s submitted work. A Local '
         + 'Chairperson reads his own territory through the ordinary rules, which is not a review '
         + 'and leaves no review record.';
  }
  return '';
}

// ---------------------------------------------------------------------------------------------
// THE RECORD. Returns { ok, errors[], record } - never a partial record.
//
// EVERY FIELD HERE ANSWERS ONE OF THE THREE CONDITIONS, and none is optional:
//   reviewerName / reviewerRole / reviewedAt   -> RECORDED (who, when)
//   subjectType / subjectId                    -> RECORDED (what)
//   reason                                     -> THE REASON WHY, the separately stated requirement
//   subordinateOffice                          -> TRANSPARENT: the office that must be able to see it
// ---------------------------------------------------------------------------------------------
export function reviewRecord(subject, reviewer, input) {
  const i = input || {};
  const errors = [];

  if (!mayReview(subject, reviewer)) {
    errors.push(reviewBlockedReason(subject, reviewer) || 'This matter is not open to your review.');
  }

  const subjectId = String((subject && (subject.id || subject.docId)) || '').trim();
  if (!subjectId) {
    errors.push('A review record has to name what was reviewed.');
  }

  const subjectType = String(i.subjectType || SUBJECTS.CLAIM);
  if (subjectType !== SUBJECTS.CLAIM && subjectType !== SUBJECTS.DISCIPLINE_CASE) {
    errors.push('"' + subjectType + '" is not something the platform records a review of.');
  }

  // THE REASON. 69.2 condition 3, and the one most likely to be softened into optional later.
  const reason = String(i.reason == null ? '' : i.reason).trim();
  if (!reason) {
    errors.push('Say why you are reviewing this. Ruling 69 requires the reason to be recorded WITH '
              + 'the review - it is a separate requirement from recording that the review happened, '
              + 'and the officer whose work this is will read it.');
  } else if (reason.length < REASON_MIN) {
    errors.push('That is too short to be a reason (' + reason.length + ' characters). The officer '
              + 'whose work this is will read it.');
  } else if (reason.length > REASON_MAX) {
    errors.push('Keep it to ' + REASON_MAX + ' characters.');
  }

  // TRANSPARENCY needs an addressee. A record nobody can see is a record of nothing, and this is
  // the condition that turns a log line into the thing ruling 69 actually asked for.
  const subordinateOffice = String(i.subordinateOffice || (subject && subject.scopeKey) || '').trim();
  if (!subordinateOffice) {
    errors.push('This matter names no subordinate office, so the review CANNOT be made transparent '
              + 'to the officer whose work it is - and ruling 69 requires that it is. Route the '
              + 'matter first.');
  }

  if (errors.length) return { ok: false, errors, record: null };

  return {
    ok: true,
    errors: [],
    record: {
      subjectType,
      subjectId,
      committee: (subject && subject.committee) || null,
      subordinateOffice,
      reviewerUid: (reviewer && reviewer.uid) || null,
      reviewerName: (reviewer && (reviewer.name || reviewer.email)) || 'Unknown',
      reviewerRole: (reviewer && reviewer.role) || 'unknown',
      reason,
      reviewedAt: i.at || new Date().toISOString(),
    },
  };
}

// WHAT THE SUBORDINATE OFFICE SEES. This is condition 1 made concrete: the officer whose work was
// reviewed reads the same facts the reviewer wrote, including the reason, unedited.
//
// THERE IS NO REDACTION HERE AND THAT IS DELIBERATE. A reason shown to the reviewer in full and to
// the reviewed officer in summary would not be transparency; it would be a second record.
export function reviewsOfOffice(records, scopeKey) {
  const key = String(scopeKey || '');
  return (Array.isArray(records) ? records : [])
    .filter(r => r && r.subordinateOffice === key)
    .slice()
    .sort((a, b) => (String(b.reviewedAt || '') < String(a.reviewedAt || '') ? -1 : 1));
}

// One line an officer can read without opening anything.
export function reviewLine(r) {
  if (!r) return '';
  return (r.reviewerName || 'An officer') + ' reviewed ' + (r.subjectId || 'a matter')
       + ' on ' + String(r.reviewedAt || '').slice(0, 10) + ' - ' + (r.reason || 'no reason recorded');
}
